/**
 * @param {unknown} iso
 * @returns {string|null}
 */
function parseIsoToDate(iso) {
  if (iso == null || iso === "") return null;
  const d = new Date(String(iso));
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * @param {unknown} iso
 * @returns {string}
 */
export function formatDeliveredStatusLine(iso) {
  const d = parseIsoToDate(iso);
  if (!d) return "Delivered";
  const formatted = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(d);
  return `Delivered on ${formatted}`;
}

/**
 * @typedef {"buyer" | "dealer" | "admin"} StoreProjectViewerRole
 */

export const SAMPLE_ORDER_REJECTED_MESSAGE = "This order is archived";

/**
 * @param {object} input
 * @param {StoreProjectViewerRole} input.viewerRole
 * @param {string|null|undefined} input.status
 * @param {boolean} input.placedWithoutDealer — `project.has_dealer === false` when status is `placed`
 * @param {boolean} [input.isSampleQuote]
 * @returns {{ variant: "button" | "info"; label: string } | null}
 */
export function resolveStoreProjectActionDock({
  viewerRole,
  status,
  placedWithoutDealer,
  isSampleQuote = false,
}) {
  const s = status == null ? null : String(status).trim().toLowerCase() || null;
  if (!s) return null;

  switch (viewerRole) {
    case "buyer":
      switch (s) {
        case "draft":
          return {
            variant: "button",
            label: "Place Order",
          };
        case "placed":
        case "dealer_accepted":
          return {
            variant: "button",
            label: "Awaiting order acceptance",
          };
        case "rejected":
          return {
            variant: "info",
            label: isSampleQuote
              ? SAMPLE_ORDER_REJECTED_MESSAGE
              : "Order could not be processed",
          };
        default:
          return null;
      }
    case "dealer":
      switch (s) {
        case "draft":
          return { variant: "info", label: "Draft" };
        case "placed":
          if (placedWithoutDealer) {
            return {
              variant: "info",
              label: "Awaiting dealer assignment",
            };
          }
          return { variant: "button", label: "Review order" };
        case "dealer_accepted":
          return {
            variant: "info",
            label: "Awaiting admin confirmation",
          };
        case "rejected":
          return {
            variant: "info",
            label: isSampleQuote
              ? SAMPLE_ORDER_REJECTED_MESSAGE
              : "Rejected",
          };
        default:
          return null;
      }
    case "admin":
      switch (s) {
        case "draft":
          return { variant: "info", label: "Draft" };
        case "placed":
          if (placedWithoutDealer) {
            return {
              variant: "info",
              label: "Placed — no dealer assigned",
            };
          }
          return {
            variant: "info",
            label: "Pending Dealer Confirmation",
          };
        case "dealer_accepted":
          return {
            variant: "info",
            label: "Pending payment or credit",
          };
        case "rejected":
          return {
            variant: "info",
            label: isSampleQuote
              ? SAMPLE_ORDER_REJECTED_MESSAGE
              : "Rejected",
          };
        default:
          return null;
      }
    default:
      return null;
  }
}

/**
 * Buyer "Place Order" / dealer "Accept": button is only clickable when API allows (`access`).
 *
 * @param {{ variant: string; }} dock
 * @param {boolean} allowFromApi — `project_actions.access`
 */
export function isInteractiveProjectDockClickable(dock, allowFromApi) {
  return dock.variant === "button" && allowFromApi;
}
